import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Check, X, Edit3 } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Admin-only CRUD panel for prim rate rules (position-based prim calc overrides).
 * - Default rule: Call Center @ 7% vekalet
 * - Admin can tweak the rate, add new positions (e.g. Avukat %15), toggle active.
 * - All rules apply inside calculate_prim: giden personnel in these positions get
 *   their individual rate instead of the row's standard "oran".
 */
export default function PrimRateRulesPanel() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [editing, setEditing] = useState({});  // { [id]: {pozisyon, rate, base, aktif, aciklama} }
  const [addOpen, setAddOpen] = useState(false);
  const [draft, setDraft] = useState({ pozisyon: "", rate: "", base: "vekalet", aktif: true, aciklama: "" });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/prim-rate-rules");
      setRules(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error("Oran kuralları yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startEdit = (rule) => {
    setEditing({
      ...editing,
      [rule.id]: {
        pozisyon: rule.pozisyon,
        rate: String(rule.rate * 100),
        base: rule.base,
        aktif: rule.aktif,
        aciklama: rule.aciklama || "",
      },
    });
  };

  const cancelEdit = (id) => {
    const next = { ...editing };
    delete next[id];
    setEditing(next);
  };

  const saveEdit = async (id) => {
    const e = editing[id];
    if (!e) return;
    const rate = Number(e.rate);
    if (!(rate >= 0 && rate <= 100)) {
      toast.error("Oran 0-100 arasında olmalı");
      return;
    }
    setSaving({ ...saving, [id]: true });
    try {
      await api.put(`/prim-rate-rules/${id}`, {
        pozisyon: e.pozisyon.trim(),
        rate: rate / 100,
        base: e.base,
        aktif: !!e.aktif,
        aciklama: e.aciklama || "",
      });
      toast.success("Kural güncellendi");
      cancelEdit(id);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Güncellenemedi");
    } finally {
      setSaving({ ...saving, [id]: false });
    }
  };

  const toggleActive = async (rule) => {
    try {
      await api.put(`/prim-rate-rules/${rule.id}`, { aktif: !rule.aktif });
      toast.success(rule.aktif ? "Pasifleştirildi" : "Aktifleştirildi");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Değiştirilemedi");
    }
  };

  const removeRule = async (rule) => {
    if (!window.confirm(`"${rule.pozisyon}" kuralını silmek istediğinize emin misiniz?`)) return;
    try {
      await api.delete(`/prim-rate-rules/${rule.id}`);
      toast.success("Silindi");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Silinemedi");
    }
  };

  const createRule = async () => {
    const rate = Number(draft.rate);
    if (!draft.pozisyon.trim()) { toast.error("Pozisyon zorunlu"); return; }
    if (!(rate >= 0 && rate <= 100)) { toast.error("Oran 0-100 arasında olmalı"); return; }
    try {
      await api.post("/prim-rate-rules", {
        pozisyon: draft.pozisyon.trim(),
        rate: rate / 100,
        base: draft.base,
        aktif: !!draft.aktif,
        aciklama: draft.aciklama || "",
      });
      toast.success("Kural eklendi");
      setDraft({ pozisyon: "", rate: "", base: "vekalet", aktif: true, aciklama: "" });
      setAddOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eklenemedi");
    }
  };

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid="prim-rate-rules-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h4 className="font-semibold text-sm text-slate-900">Prim Oran Kuralları (Pozisyon Bazlı)</h4>
          <p className="text-xs text-slate-600 mt-0.5">
            Belirli bir pozisyondaki personel için satırın standart <code className="bg-slate-200 px-1 rounded">oranı</code> yerine özel oran uygulanır. Örn: <strong>Call Center → %7</strong>.
          </p>
        </div>
        <button
          data-testid="rate-rule-add-btn"
          onClick={() => setAddOpen((v) => !v)}
          className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white px-3 py-1.5 rounded-md text-xs font-semibold"
        >
          <Plus size={14} /> Kural Ekle
        </button>
      </div>

      {addOpen && (
        <div className="mb-3 p-3 bg-white border border-slate-300 rounded-lg" data-testid="rate-rule-add-form">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
            <input
              data-testid="new-pozisyon"
              placeholder="Pozisyon (örn: Avukat)"
              value={draft.pozisyon}
              onChange={(e) => setDraft({ ...draft, pozisyon: e.target.value })}
              className="col-span-2 px-2 py-1.5 border border-slate-200 rounded text-sm"
            />
            <div className="flex items-center gap-1">
              <input
                data-testid="new-rate"
                type="number"
                step="0.01"
                min="0"
                max="100"
                placeholder="Oran"
                value={draft.rate}
                onChange={(e) => setDraft({ ...draft, rate: e.target.value })}
                className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm text-right font-mono-tab"
              />
              <span className="text-xs text-slate-600">%</span>
            </div>
            <select
              data-testid="new-base"
              value={draft.base}
              onChange={(e) => setDraft({ ...draft, base: e.target.value })}
              className="px-2 py-1.5 border border-slate-200 rounded text-sm"
            >
              <option value="vekalet">Vekalet</option>
              <option value="tahsilat">Tahsilat</option>
            </select>
            <div className="flex items-center gap-2">
              <button
                data-testid="new-save"
                onClick={createRule}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-1.5 rounded text-xs font-semibold"
              >
                Kaydet
              </button>
              <button
                onClick={() => setAddOpen(false)}
                className="px-2 py-1.5 rounded border border-slate-200 text-xs"
              >
                İptal
              </button>
            </div>
          </div>
          <input
            placeholder="Açıklama (opsiyonel)"
            value={draft.aciklama}
            onChange={(e) => setDraft({ ...draft, aciklama: e.target.value })}
            className="mt-2 w-full px-2 py-1.5 border border-slate-200 rounded text-sm"
          />
        </div>
      )}

      {loading ? (
        <div className="text-center py-6 text-slate-400 text-sm">Yükleniyor...</div>
      ) : rules.length === 0 ? (
        <div className="text-center py-6 text-slate-400 text-sm">Henüz kural tanımlı değil</div>
      ) : (
        <div className="overflow-x-auto bg-white rounded-md border border-slate-200">
          <table className="w-full text-sm" data-testid="rate-rules-table">
            <thead className="bg-slate-100 text-xs uppercase tracking-wider text-slate-600">
              <tr>
                <th className="text-left px-3 py-2">Pozisyon</th>
                <th className="text-right px-3 py-2">Oran</th>
                <th className="text-left px-3 py-2">Baz</th>
                <th className="text-center px-3 py-2">Durum</th>
                <th className="text-left px-3 py-2">Açıklama</th>
                <th className="text-right px-3 py-2">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {rules.map((rule) => {
                const e = editing[rule.id];
                if (e) {
                  return (
                    <tr key={rule.id} data-testid={`rate-rule-edit-${rule.id}`} className="border-t border-slate-200 bg-amber-50">
                      <td className="px-3 py-2">
                        <input
                          data-testid={`edit-pozisyon-${rule.id}`}
                          value={e.pozisyon}
                          onChange={(ev) => setEditing({ ...editing, [rule.id]: { ...e, pozisyon: ev.target.value } })}
                          className="w-full px-2 py-1 border border-slate-300 rounded text-sm"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1 justify-end">
                          <input
                            data-testid={`edit-rate-${rule.id}`}
                            type="number"
                            step="0.01"
                            min="0"
                            max="100"
                            value={e.rate}
                            onChange={(ev) => setEditing({ ...editing, [rule.id]: { ...e, rate: ev.target.value } })}
                            className="w-20 px-2 py-1 border border-slate-300 rounded text-sm text-right font-mono-tab"
                          />
                          <span className="text-xs">%</span>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <select
                          data-testid={`edit-base-${rule.id}`}
                          value={e.base}
                          onChange={(ev) => setEditing({ ...editing, [rule.id]: { ...e, base: ev.target.value } })}
                          className="px-2 py-1 border border-slate-300 rounded text-sm"
                        >
                          <option value="vekalet">Vekalet</option>
                          <option value="tahsilat">Tahsilat</option>
                        </select>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <input
                          data-testid={`edit-aktif-${rule.id}`}
                          type="checkbox"
                          checked={!!e.aktif}
                          onChange={(ev) => setEditing({ ...editing, [rule.id]: { ...e, aktif: ev.target.checked } })}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          value={e.aciklama}
                          onChange={(ev) => setEditing({ ...editing, [rule.id]: { ...e, aciklama: ev.target.value } })}
                          className="w-full px-2 py-1 border border-slate-300 rounded text-sm"
                        />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="inline-flex gap-1">
                          <button
                            data-testid={`save-edit-${rule.id}`}
                            onClick={() => saveEdit(rule.id)}
                            disabled={saving[rule.id]}
                            className="w-7 h-7 inline-flex items-center justify-center rounded bg-emerald-600 hover:bg-emerald-700 text-white"
                          >
                            <Check size={14} />
                          </button>
                          <button
                            onClick={() => cancelEdit(rule.id)}
                            className="w-7 h-7 inline-flex items-center justify-center rounded border border-slate-200 text-slate-500"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                }
                return (
                  <tr key={rule.id} data-testid={`rate-rule-row-${rule.id}`} className="border-t border-slate-200 hover:bg-slate-50">
                    <td className="px-3 py-2 font-semibold text-slate-900">{rule.pozisyon}</td>
                    <td className="px-3 py-2 text-right font-mono-tab font-semibold text-slate-900">
                      %{(rule.rate * 100).toFixed(2)}
                    </td>
                    <td className="px-3 py-2 text-slate-600">
                      {rule.base === "vekalet" ? "Vekalet Ücreti" : "Tahsilat"}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <button
                        data-testid={`toggle-active-${rule.id}`}
                        onClick={() => toggleActive(rule)}
                        className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${rule.aktif ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}
                      >
                        {rule.aktif ? "Aktif" : "Pasif"}
                      </button>
                    </td>
                    <td className="px-3 py-2 text-slate-600 text-xs">{rule.aciklama || "—"}</td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex gap-1">
                        <button
                          data-testid={`edit-rule-${rule.id}`}
                          onClick={() => startEdit(rule)}
                          className="w-7 h-7 inline-flex items-center justify-center rounded text-slate-600 hover:bg-slate-100"
                        >
                          <Edit3 size={14} />
                        </button>
                        <button
                          data-testid={`delete-rule-${rule.id}`}
                          onClick={() => removeRule(rule)}
                          className="w-7 h-7 inline-flex items-center justify-center rounded text-rose-600 hover:bg-rose-50"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
